import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { unlockScroll } from './top-utils.js';

let animationInitialized = false;
let backgroundFadeOutInitialized = false;

/* ====================================
# 背景要素フェードインアニメーション
==================================== */
export const initBgFadeIn = () => {
  const bgElements = [
    document.querySelector('.p-top-hero__bg01'),
    document.querySelector('.p-top-hero__bg02'),
    document.querySelector('.p-top-hero__bg03')
  ];

  bgElements.forEach((bgElement, index) => {
    if (bgElement) {
      // 各要素ごとに異なる方向から登場させる初期位置を設定
      let initialDirection = {};

      if (index === 0) { // bg01 - 左下から
        initialDirection = { x: -200, y: 200 };
      } else if (index === 1) { // bg02 - 右上から
        initialDirection = { x: 200, y: -200 };
      } else if (index === 2) { // bg03 - 右下から
        initialDirection = { x: 200, y: 200 };
      }

      // 初期位置を設定（外から登場）
      gsap.set(bgElement, {
        x: initialDirection.x,
        y: initialDirection.y,
        opacity: 0
      });

      // 現在の位置にフェードインアニメーション
      gsap.to(bgElement, {
        x: 0,
        y: 0,
        opacity: 1,
        duration: 3,
        ease: "power2.out",
        delay: index * 0.08 // 少しずつずらして登場
      });
    }
  });
};

/* ====================================
# 背景要素フェードアウトアニメーション
==================================== */
export const initBackgroundFadeOut = () => {
  if (backgroundFadeOutInitialized) return; // 既に初期化済みの場合は何もしない
  backgroundFadeOutInitialized = true;
  const bgElements = [
    document.querySelector('.p-top-hero__bg01'),
    document.querySelector('.p-top-hero__bg02'),
    document.querySelector('.p-top-hero__bg03')
  ];

  bgElements.forEach((bgElement, index) => {
    if (bgElement) {
      // アニメーション実行フラグ（重複実行防止）
      let animationExecuted = false;

      // アニメーション実行関数
      const executeAnimation = () => {
        // 既に非表示の場合は実行しない
        if (bgElement.style.opacity === '0') return;
        animationExecuted = true;

        // 各要素ごとに異なる方向に動くアニメーション
        let direction = {};

        if (index === 0) { // bg01 - 左下に
          direction = { x: -200, y: 200 };
        } else if (index === 1) { // bg02 - 右上に
          direction = { x: 200, y: -200 };
        } else if (index === 2) { // bg03 - 右下に
          direction = { x: 200, y: 200 };
        }

        // パッと逃げるアニメーション
        gsap.to(bgElement, {
          x: direction.x,
          y: direction.y,
          opacity: 0,
          duration: 1,
          ease: "power2.out"
        });
      };

      // スクロールトリガーでアニメーション開始のタイミングを検知
      ScrollTrigger.create({
        trigger: '.p-top-hero',
        start: 'top bottom', // 画面下部に入った瞬間
        end: 'bottom top',
        onEnter: executeAnimation,
        onLeave: executeAnimation,
        onUpdate: (self) => {
          // スクロール位置が発火条件を超えていたら即座に実行
          if (self.progress > 0) {
            executeAnimation();
          }
        }
      });
    }
  });
};

/* ====================================
# テキストアニメーション
==================================== */
export const initTextAnimation = (isFirstVisit = true) => {
  // 重複実行防止
  if (animationInitialized) {
    return;
  }
  animationInitialized = true;

  const heroText = document.querySelector('.p-top-hero__text');
  const mobileSubElement = document.querySelector('.p-top-hero__sub--sp');

  if (!heroText) {
    // テキスト要素がない場合は直接ハイライトアニメーション開始
    initHighlightAnimation(isFirstVisit);
    return;
  }

  if (!isFirstVisit) {
    // 2回目以降は既にshowMainContentDirectly()でクラスを設定済み
    // ハイライトアニメーションのみ完了状態にする
    initHighlightAnimation(false); // falseで完了状態を指定
    return;
  }

  // 初回訪問時のアニメーション
  // アニメーションを確実にリセットして初期状態にする
  heroText.classList.remove('is-animated');
  if (mobileSubElement) {
    mobileSubElement.classList.remove('is-animated');
  }

  // アニメーション開始のタイミングを調整
  setTimeout(() => {
    // ヒーローテキストのアニメーション開始
    heroText.classList.add('is-animated');

    // スマホの場合は同時にサブテキストもアニメーション開始
    if (window.innerWidth <= 768 && mobileSubElement) {
      mobileSubElement.classList.add('is-animated');
    }

    // テキストアニメーション完了後にハイライトアニメーション開始
    setTimeout(() => {
      initHighlightAnimation(true); // trueでアニメーション実行
    }, 800); // CSSのtransition durationと合わせる
  }, 200); // 少し短縮して200ms後に開始
};

/* ====================================
# ハイライト動画アニメーション
==================================== */
export const initHighlightAnimation = (playAnimation = true) => {
  const highlightElement = document.querySelector('.p-top-hero__highlight');
  const highlightVideo = document.querySelector('.p-top-hero__highlight video');

  if (highlightElement) {
    // 重複実行防止のため、既にアニメーション済みかチェック
    if (highlightElement.classList.contains('is-animating')) {
      return;
    }

    // 前回のアニメーションをリセット
    highlightElement.classList.remove('is-animating', 'is-ready');

    // 2回目以降の訪問または完了状態を指定された場合
    if (!playAnimation) {
      const catchElement = document.querySelector('.p-top-hero__catch');

      // textとsub、highlight要素を初期非表示にする
      const textElement = catchElement.querySelector('.p-top-hero__text');
      const subElement = catchElement.querySelector('.p-top-hero__sub--sp');

      // highlightのクラスを全て削除してCSS制御を防ぐ
      highlightElement.className = 'p-top-hero__highlight';

      // 初期状態で非表示（transition無効化）
      gsap.set([textElement, subElement, highlightElement], {
        opacity: 0,
        visibility: 'visible',
        transition: 'none'
      });

      // textとsubは最初から0の位置に配置（初回のマイナス位置をリセット）
      if (textElement) {
        gsap.set(textElement, { transform: 'translate3d(0, 0, 0)' });
      }
      if (subElement) {
        gsap.set(subElement, { transform: 'translate3d(0, 0, 0)' });
      }

      if (highlightVideo) {
        // poster画像のURLを取得してimg要素に置き換え
        const posterUrl = highlightVideo.getAttribute('poster');
        if (posterUrl) {
          const img = document.createElement('img');
          img.src = posterUrl;

          // 動画をimg要素に置き換え
          highlightVideo.parentNode.replaceChild(img, highlightVideo);
        }
      }

      // 読み込み関係なくsetTimeoutで同時フェード
      setTimeout(() => {
        gsap.to([textElement, subElement, highlightElement], {
          opacity: 1,
          duration: 0.3,
          ease: 'none'
        });
      }, 500);

      // スクロールロック解除と背景フェードアウト
      unlockScroll();
      const bgElements = document.querySelectorAll('.p-top-hero__bg01, .p-top-hero__bg02, .p-top-hero__bg03');
      const needsBgFadeOut = Array.from(bgElements).some(bg => bg && bg.style.opacity !== '0');
      if (needsBgFadeOut) {
        initBackgroundFadeOut();
      }
      return;
    }

    // 初回訪問時のアニメーション

    // まずハイライト要素を表示
    highlightElement.classList.add('is-ready');

    // video要素がある場合は一旦停止して最初に戻す
    if (highlightVideo) {
      highlightVideo.pause();
      highlightVideo.currentTime = 0;
    }

    // ハイライト要素の表示完了後にアニメーション開始
    setTimeout(() => {
      highlightElement.classList.add('is-animating');

      // video要素がある場合は再生を制御
      if (highlightVideo) {
        // 動画終了時にスクロールロック解除
        const handleVideoEnd = () => {
          unlockScroll();

          // 背景フェードアウト開始
          const bgElements = document.querySelectorAll('.p-top-hero__bg01, .p-top-hero__bg02, .p-top-hero__bg03');
          const needsBgFadeOut = Array.from(bgElements).some(bg => bg && bg.style.opacity !== '0');
          if (needsBgFadeOut) {
            initBackgroundFadeOut();
          }
          // イベントリスナーを削除
          highlightVideo.removeEventListener('ended', handleVideoEnd);
        };

        // 動画終了イベントリスナーを追加
        highlightVideo.addEventListener('ended', handleVideoEnd);

        // 少し遅延してから再生開始（CSSのopacityトランジションを待つ）
        setTimeout(() => {
          highlightVideo.play();
        }, 200);

        // フォールバック：1秒後に強制的にスクロールロック解除（モバイル用に短縮）
        setTimeout(() => {
          if (document.body.classList.contains('scroll-locked')) {
            handleVideoEnd();
          }
        }, 1000);
      } else {
        // video要素がない場合は1秒後にスクロールロック解除
        setTimeout(() => {
          unlockScroll();

          // 背景フェードアウト開始
          const bgElements = document.querySelectorAll('.p-top-hero__bg01, .p-top-hero__bg02, .p-top-hero__bg03');
          const needsBgFadeOut = Array.from(bgElements).some(bg => bg && bg.style.opacity !== '0');
          if (needsBgFadeOut) {
            initBackgroundFadeOut();
          }
        }, 1000);
      }
    }, 500); // ハイライト要素の表示完了を待つ
  }
};